import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedMarketText, localizedRecordLabel } from '@/i18n/catalogDisplayText';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import TiktokSyncProgressBar from '@/tiktok-platform/components/TiktokSyncProgressBar';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { downloadCsv } from '@/tiktok-platform/modules/products/components/TiktokPageToolbar';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import { openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import {
  batchPublishDrafts,
  TIKTOK_BATCH_PUBLISH_CONCURRENCY,
} from '@/tiktok-platform/utils/tiktokBatchPublishDrafts';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

const DRAFT_EXPORT_HEADERS = [
  'id', 'title', 'supplierCode', 'platformSpuName', 'siteLabel', 'source', 'createdAt', 'updatedAt',
];

function formatBeijingDateTime(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n) => String(n).padStart(2, '0');
  // 后端常把北京墙钟标成 UTC(Z)，用 UTC 分量即北京时间；否则按 Asia/Shanghai
  const mislabeledAsUtc = /T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+]00:00)$/i.test(raw);
  const opts = {
    timeZone: mislabeledAsUtc ? 'UTC' : 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '00';
  return `${get('year')}/${get('month')}/${get('day')} ${pad(Number(get('hour')))}:${get('minute')}:${get('second')}`;
}

function DraftSkcThumb({ imageUrl }) {
  return <TiktokProductThumb imageUrl={imageUrl} />;
}

/** 发品草稿箱：产品信息 / SKC 主图 */
function stripSkuIdentity(sku) {
  if (!sku || typeof sku !== 'object') return sku;
  const next = { ...sku };
  delete next.id;
  delete next.skuId;
  delete next.sku_id;
  delete next.productSkuId;
  return next;
}

/** 复制成新的本地草稿：去掉平台商品/SKU 标识，避免之后发布时改到原商品。 */
function cloneDraftForm(form) {
  const source = form && typeof form === 'object' ? form : {};
  const next = JSON.parse(JSON.stringify(source));
  const title = String(next.title || next.productName || '').trim();
  const copyTitle = title ? `${title} 副本` : '未命名草稿 副本';
  next.title = copyTitle;
  if (next.productName) next.productName = copyTitle;
  ['productId', 'platformProductId', 'spuName', 'platformSpuName', 'publishedSpu'].forEach((key) => {
    delete next[key];
  });
  if (Array.isArray(next.skcList)) {
    next.skcList = next.skcList.map((skc) => {
      if (!skc || typeof skc !== 'object') return skc;
      const copy = { ...skc };
      delete copy.skcId;
      delete copy.productSkcId;
      ['skuRows', 'skuList'].forEach((key) => {
        if (Array.isArray(copy[key])) copy[key] = copy[key].map(stripSkuIdentity);
      });
      return copy;
    });
  }
  if (Array.isArray(next.skuList)) next.skuList = next.skuList.map(stripSkuIdentity);
  return { form: next, title: copyTitle };
}

export default function ProductDraftsPage() {
  const { t, i18n } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId, allShopsScope, activeShop } = useTiktokPageShopScope();
  const shopId = concreteScopeId;

  const [filterDraft, setFilterDraft] = useState({ title: '', supplierCode: '' });
  const [query, setQuery] = useState({ keyword: '' });
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [batchPublishProgress, setBatchPublishProgress] = useState(null);
  const selectedRowsRef = useRef(new Map());

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedIds.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      if (row?.id != null && selectedIds.has(row.id)) map.set(row.id, row);
    });
  }, [items, selectedIds]);

  const loadDrafts = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await tiktokPlatformAPI.listPublishDrafts(shopId, {
        keyword: query.keyword || undefined,
        page,
        pageSize,
      });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      setTotal(Number(res?.total) || 0);
      // 跨页选中：翻页刷新列表时保留已选 id
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('draftBox.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, query.keyword, page, pageSize, queryNonce, t]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  useEffect(() => {
    setFilterDraft({ title: '', supplierCode: '' });
    setQuery({ keyword: '' });
    setPage(1);
    setQueryNonce((n) => n + 1);
    setSelectedIds(new Set());
  }, [shopId]);

  const selectableIds = useMemo(() => items.map((row) => row.id).filter(Boolean), [items]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => togglePageSelection(prev, selectableIds, allSelected));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onQuery = () => {
    const parts = [filterDraft.title, filterDraft.supplierCode]
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (shopId) setLoading(true);
    setPage(1);
    setQuery({ keyword: parts.join(' ') });
    setQueryNonce((n) => n + 1);
  };

  const onReset = () => {
    setFilterDraft({ title: '', supplierCode: '' });
    setQuery({ keyword: '' });
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const [copyingId, setCopyingId] = useState('');

  const onCopy = async (item) => {
    if (!shopId || !item?.id || copyingId) return;
    setCopyingId(String(item.id));
    try {
      const detail = await tiktokPlatformAPI.getPublishDraft(shopId, item.id);
      const form = detail?.form && typeof detail.form === 'object' ? detail.form : null;
      if (!form) {
        showToast(t('draftBox.copyMissing'), 'warning');
        return;
      }
      const cloned = cloneDraftForm(form);
      await tiktokPlatformAPI.createPublishDraft(shopId, {
        kind: 'DRAFT',
        title: cloned.title,
        form: cloned.form,
      });
      showToast(t('draftBox.copied'), 'success');
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('draftBox.copyFailed'), 'error');
    } finally {
      setCopyingId('');
    }
  };

  const onContinue = (item) => {
    if (item?.source === 'sync' && item?.platformSpuName) {
      const qs = new URLSearchParams();
      if (item.id != null) qs.set('draftId', String(item.id));
      const q = qs.toString();
      openTiktokPathInNewTab(
        `products/edit/${encodeURIComponent(item.platformSpuName)}${q ? `?${q}` : ''}`,
      );
      return;
    }
    openTiktokPathInNewTab(`products/publish?draftId=${encodeURIComponent(item.id)}`);
  };

  const onDelete = async (id) => {
    if (!shopId) return;
    const ok = await showTiktokConfirm({
      title: t('draftBox.confirmDelete'),
      message: t('draftBox.deleteOne', { platform: 'TikTok' }),
      tone: 'danger',
      confirmText: t('draftBox.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    try {
      await tiktokPlatformAPI.deletePublishDraft(shopId, id);
      showToast(t('draftBox.deleted'), 'success');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      loadDrafts();
    } catch (e) {
      showToast(e?.message || t('draftBox.deleteFailed'), 'error');
    }
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('draftBox.pickToDelete'), 'warning');
      return;
    }
    const ids = Array.from(selectedIds)
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    if (!ids.length) {
      showToast(t('draftBox.invalidSelection'), 'warning');
      return;
    }
    const confirmed = await showTiktokConfirm({
      title: t('draftBox.batchDelete'),
      message: t('draftBox.batchDeleteMsg', { count: ids.length, platform: 'TikTok' }),
      tone: 'danger',
      confirmText: t('draftBox.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    try {
      const res = await tiktokPlatformAPI.batchDeletePublishDrafts(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('draftBox.deletedCount', { count }), 'success');
      } else {
        showToast(t('draftBox.noneDeleted'), 'warning');
      }
      setSelectedIds(new Set());
      loadDrafts();
    } catch (e) {
      showToast(e?.message || t('draftBox.batchDeleteFailed'), 'error');
    } finally {
      setToolbarBusy(false);
    }
  };

  const writeDraftsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      DRAFT_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        supplierCode: row.supplierCode,
        platformSpuName: row.platformSpuName,
        siteLabel: row.siteLabel || '',
        source: row.source,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
      })),
    );
  }, []);

  const fetchAllDrafts = useCallback(async () => {
    if (!shopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await tiktokPlatformAPI.listPublishDrafts(shopId, {
        keyword: query.keyword || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [shopId, query.keyword]);

  const exportFilteredDrafts = useCallback(async () => {
    if (!shopId) {
      showToast(t('draftBox.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllDrafts();
      if (!rows.length) {
        showToast(t('draftBox.noneToExport'), 'warning');
        return;
      }
      writeDraftsCsv(rows, `tiktok-drafts-filtered-${Date.now()}.csv`);
      showToast(t('draftBox.exported', { count: rows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('draftBox.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, fetchAllDrafts, writeDraftsCsv, t]);

  const exportPageDrafts = useCallback(() => {
    if (!items.length) {
      showToast(t('draftBox.noneToExport'), 'warning');
      return;
    }
    writeDraftsCsv(items, `tiktok-drafts-page-${Date.now()}.csv`);
    showToast(t('draftBox.exported', { count: items.length }), 'success');
  }, [items, writeDraftsCsv, t]);

  const exportSelectedDrafts = useCallback(() => {
    const rows = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (!rows.length) {
      showToast(t('draftBox.pickToExport'), 'warning');
      return;
    }
    writeDraftsCsv(rows, `tiktok-drafts-selected-${Date.now()}.csv`);
    showToast(t('draftBox.exportedSelected', { count: rows.length }), 'success');
  }, [selectedIds, writeDraftsCsv, t]);

  const onBatchPublish = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('draftBox.pickToPublish'), 'warning');
      return;
    }
    if (toolbarBusy) return;
    const selected = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id) || { id })
      .filter((item) => item?.id);
    if (!selected.length) {
      showToast(t('draftBox.pickToPublish'), 'warning');
      return;
    }
    const confirmed = await showTiktokConfirm({
      title: t('draftBox.batchPublish'),
      message: t('draftBox.batchPublishMsg', { count: selected.length, limit: TIKTOK_BATCH_PUBLISH_CONCURRENCY }),
      tone: 'warning',
      confirmText: t('draftBox.startPublish'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;

    setToolbarBusy(true);
    setBatchPublishProgress({
      visible: true,
      status: 'running',
      percent: 1,
      processed: 0,
      total: selected.length,
      message: t('draftBox.preparing'),
    });

    try {
      const result = await batchPublishDrafts({
        shopId,
        shopType: activeShop?.shopType,
        drafts: selected,
        concurrency: TIKTOK_BATCH_PUBLISH_CONCURRENCY,
        onProgress: ({ processed, total, message }) => {
          const safeTotal = Math.max(1, Number(total) || selected.length);
          const done = Math.max(0, Number(processed) || 0);
          setBatchPublishProgress({
            visible: true,
            status: 'running',
            percent: Math.min(99, Math.round((done / safeTotal) * 100)),
            processed: done,
            total: safeTotal,
            message: message || t('draftBox.publishing'),
          });
        },
      });

      const okCount = result.success.length;
      const failCount = result.failed.length;
      setBatchPublishProgress({
        visible: true,
        status: failCount && !okCount ? 'failed' : 'success',
        percent: 100,
        processed: result.total,
        total: result.total,
        message: t('draftBox.done', { ok: okCount, fail: failCount }),
      });

      if (okCount) {
        const okIds = new Set(result.success.map((row) => row.draftId));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          okIds.forEach((id) => next.delete(id));
          return next;
        });
        loadDrafts();
      }

      if (okCount && !failCount) {
        showToast(t('draftBox.submitted', { count: okCount }), 'success');
      } else if (okCount && failCount) {
        const sample = result.failed.slice(0, 3)
          .map((row) => t('draftBox.failRow', { title: row.title || row.draftId, message: row.message || t('draftBox.failed') }))
          .join(t('draftBox.failSep'));
        showToast(t('draftBox.partial', { ok: okCount, fail: failCount, sample, more: failCount > 3 ? '…' : '' }), 'warning');
      } else {
        const sample = result.failed.slice(0, 3)
          .map((row) => t('draftBox.failRow', { title: row.title || row.draftId, message: row.message || t('draftBox.failed') }))
          .join(t('draftBox.failSep'));
        showToast(t('draftBox.allFailed', { sample, more: failCount > 3 ? '…' : '' }), 'error');
      }
    } catch (e) {
      setBatchPublishProgress({
        visible: true,
        status: 'failed',
        percent: 0,
        processed: 0,
        total: selected.length,
        message: e?.message || t('draftBox.batchPublishFailed'),
      });
      showToast(e?.message || t('draftBox.batchPublishFailed'), 'error');
    } finally {
      setToolbarBusy(false);
      window.setTimeout(() => setBatchPublishProgress(null), 1800);
    }
  };

  const colSpan = 7;

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedIds.size}
            busy={toolbarBusy || loading || exporting}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFilteredDrafts,
              onExportPage: exportPageDrafts,
              onExportSelected: exportSelectedDrafts,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || toolbarBusy}
            extraActions={[
              {
                key: 'publish',
                label: t('draftBox.batchPublish'),
                tone: 'primary',
                disabled: !shopId || selectedIds.size === 0 || toolbarBusy,
                onClick: onBatchPublish,
              },
            ]}
          />
        )}
      >
        {batchPublishProgress?.visible ? (
          <div style={{ marginBottom: 12 }}>
            <TiktokSyncProgressBar progress={batchPublishProgress} />
          </div>
        ) : null}
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline">
              <span className="tko-field__label">{t('draftBox.title')}</span>
              <input
                className="tko-input"
                value={filterDraft.title}
                placeholder={t('draftBox.titlePlaceholder')}
                disabled={!shopId}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <div className="tko-field tko-field--inline">
              <span className="tko-field__label">{t('draftBox.supplier')}</span>
              <input
                className="tko-input"
                value={filterDraft.supplierCode}
                placeholder={t('draftBox.supplierPlaceholder')}
                disabled={!shopId}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>

          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={onReset} disabled={!shopId}>{t('draftBox.reset')}</button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={onQuery} disabled={!shopId}>{t('draftBox.query')}</button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('draftBox.querying')}
      >
        <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
          <div className="wf-tasks-table-panel">
            <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
              <table className="wf-tasks-table platform-product-table platform-product-table--tiktok platform-product-table--drafts">
                <colgroup>
                  <col className="tiktok-product-col--check" />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="tiktok-product-cell--spu-check"
                      onClick={() => {
                        if (!shopId || loading || selectableIds.length === 0) return;
                        toggleAll();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('draftBox.selectAll')}
                        disabled={!shopId || loading || selectableIds.length === 0}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">{t('draftBox.productInfo')}</th>
                    <th scope="col">SKC</th>
                    <th scope="col">{t('draftBox.site')}</th>
                    <th scope="col">{t('draftBox.type')}</th>
                    <th scope="col" className="tiktok-draft-th--time">{t('draftBox.createdAt')}</th>
                    <th scope="col" className="tiktok-draft-th--actions">{t('draftBox.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!shopId || loading || items.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="wf-tasks-table__empty">
                        <div className="funding-empty-state">
                          <p className="funding-empty-state__title">
                            {!shopId ? t('draftBox.pickShop') : loading ? t('draftBox.loading') : t('draftBox.noDrafts')}
                          </p>
                          {!shopId || loading ? null : (
                            <p className="funding-empty-state__description">
                              {t('draftBox.emptyHint')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.flatMap((item) => {
                      const skcItems = Array.isArray(item.skcItems) && item.skcItems.length
                        ? item.skcItems
                        : [{ colorLabel: null, skcName: null, imageUrl: null, supplierCode: null }];
                      const typeLabel = item.typeLabel
                        || (item.source === 'sync' ? t('draftBox.sourceSync') : t('draftBox.sourceNew'));
                      const rowSpan = Math.max(1, skcItems.length);

                      return skcItems.map((skc, skcIdx) => {
                        const isFirst = skcIdx === 0;

                        return (
                          <tr
                            key={`${item.id}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
                            className={`tiktok-product-row tiktok-draft-row${isFirst ? '' : ' tiktok-draft-row--skc-child'}`}
                          >
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-product-cell--spu-check"
                                  onClick={() => toggleOne(item.id)}
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={t('draftBox.selectDraft', { title: item.title || item.id })}
                                    checked={selectedIds.has(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleOne(item.id)}
                                  />
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-product-cell--info"
                                >
                                  <div className="tiktok-product-info tiktok-product-info--text-only tiktok-draft-product-info">
                                    <div className="tiktok-product-info__text">
                                      <HoverCopyField
                                        value={item.title || t('draftBox.unnamed')}
                                        className="tiktok-product-info__title"
                                        popoverCopy
                                      />
                                      <HoverCopyField
                                        label={t('tiktokCatalog.productId')}
                                        value={item.productId || item.platformProductId || item.platformSpuName || ''}
                                        className="tiktok-product-info__meta"
                                        separator=": "
                                        emptyText="—"
                                      />
                                      <div className="tiktok-product-info__meta">
                                        {t('draftBox.category', { label: item.categoryLabel || '—' })}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </>
                            ) : null}

                            <td className="tiktok-product-cell tiktok-product-cell--spec">
                              <div className="tiktok-product-spec tiktok-draft-skc">
                                <DraftSkcThumb imageUrl={skc.imageUrl} />
                                <div className="tiktok-product-spec__text">
                                  <div className="tiktok-product-spec__color">
                                    {skc.colorLabel || '—'}
                                  </div>
                                  <HoverCopyField
                                    label="SKU ID"
                                    value={skc.skuId || skc.sku_id || ''}
                                    className="tiktok-product-spec__skc"
                                    separator=": "
                                    emptyText="—"
                                  />
                                  <HoverCopyField
                                    label={t('draftBox.supplier')}
                                    value={skc.supplierCode || ''}
                                    className="tiktok-product-spec__skc"
                                    separator=": "
                                    emptyText="—"
                                  />
                                </div>
                              </div>
                            </td>

                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-draft-cell--site"
                                >
                                  {localizedMarketText(item.siteLabel, i18n.language) || '—'}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-draft-cell--type"
                                >
                                  {localizedRecordLabel(t, typeLabel, i18n.language)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell platform-product-table__muted tiktok-draft-cell--time"
                                >
                                  {formatBeijingDateTime(item.createdAt || item.updatedAt)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-draft-cell--actions"
                                >
                                  <div className="tiktok-draft-actions">
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={copyingId === String(item.id)}
                                      onClick={() => onCopy(item)}
                                    >{copyingId === String(item.id) ? t('draftBox.copying') : t('draftBox.copy')}</button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      onClick={() => onContinue(item)}
                                    >{t('draftBox.edit')}</button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      onClick={() => onDelete(item.id)}
                                    >{t('draftBox.delete')}</button>
                                  </div>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <TiktokOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            disabled={!shopId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('draftBox.pager')}
          />
        </div>
      </ProductListCard>
    </>
  );
}
